import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import iconBack from '../../../images/icon-chevron-left.svg';
import { getLocationById } from '../../../redux/location/actions/location.action';
import { fetchLockerWiseAvailabilityAndAppointments } from '../../../redux/locker-wise-availability/actions/lockerWiseAvailability.actions';
import {
    LockerWiseAvailabilityAndAppointmentsResponse,
    LockerWiseAvailabilityAndAppointmentsState,
} from '../../../redux/locker-wise-availability/interfaces/lockerWiseAvailability.interface';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getUnit } from '../../../redux/visitor-list/actions/createAppoinment.action';
import { generateTimeSlots } from '../../../utils/utils';
import Loader from '../../common/loader/Loader';
import Toast from '../../common/toast/Toast';
import AvailabilityHeader from './availability-header/AvailabilityHeader';
import DifferentUnitAvailability from './different-unit-availability/DifferentUnitAvailability';
import SelectedUnitAvailability from '../selected-unit-availability/SelectedUnitAvailability';
import styles from './AddNewLocationAvailability.module.css';
import Accordion from '../../accordion';
import CustomUnitAvailability from '../custom-unit-availabilty';
import { getContinuousTimeRanges, getTimeRangeFromSlots } from '../../../utils/getContinuousTimeRanges';
import '../../../index.css';
import { AvailabilityProps, FinalGroupedSlot } from './types';

/**
 * Optimized Add Availability Component for New Location Flow
 * 
 * Features:
 * - Proper error and loading state management
 * - Memoized computations for better performance
 * - Improved error handling with user feedback
 * - Optimized re-renders
 * - Better code organization
 */

const AddNewLocationAvailability = () => {
    const locationState = useLocation().state as {
        unitId?: string;
        locationId?: string;
        type?: string;
    };

    const { unitId, locationId, type = 'add-availability' } = locationState || {};

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    // State management
    const [selectedDate, setSelectedDate] = useState(new Date(moment().format()));
    const [activeTab, setActiveTab] = useState(0);
    const [differentUnitsAvailabilities, setDifferentUnitsAvailabilities] = useState<AvailabilityProps[]>();
    const [groupedDifferentUnitsAvailabilities, setGroupedDifferentUnitsAvailabilities] = useState<FinalGroupedSlot[] | null>(null);
    const [slotRange, setSlotRange] = useState('');
    const [selectedWeekDay, setSelectedWeekDay] = useState(moment(new Date()).locale('en').format('dddd'));
    const [officeHoursOfWeekDay, setOfficeHoursOfWeekDay] = useState<{ label: string; value: string }[]>([]);
    const [isOfficeHoursChecked, setIsOfficeHoursChecked] = useState(true);
    const [satSunAvailability, setSatSunAvailability] = useState(false);
    const [activeIndex, setActiveIndex] = useState<null | number>(null);
    const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
    const [sundaySlots, setSundaySlots] = useState<number[]>([]);
    const [saturdaySlots, setSaturdaySlots] = useState<number[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);

    // Redux selectors
    const { data, loading: unitLoading, error: unitError } = useSelector((state: IRootState) => state.createAppoinmentReducer.unit);
    const lockerWiseAvailabilityAndAppointmentsData = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).data;
    const locationData = useSelector((state: IRootState) => state.locationReducer.location.data);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.location.loading);
    const locationError = useSelector((state: IRootState) => state.locationReducer.location.errorMsg);

    const isLoading = unitLoading || locationLoading;
    const hasError = unitError || locationError;

    // Transform data function - memoized for performance
    const transformData = useCallback((
        data: LockerWiseAvailabilityAndAppointmentsResponse[],
        selectedDate: string,
        lockerFilter: string,
        kioskType = 'kiosk',
        unitId = ''
    ): FinalGroupedSlot[] => {
        const groupedByUnit = new Map<string, FinalGroupedSlot>();
        const selectedMoment = moment(selectedDate).format('YYYY-MM-DD');

        data.forEach(item => {
            if (kioskType?.toLowerCase() === 'bms' && unitId) {
                if (item.unitId?.toString() !== unitId?.toString()) {
                    return;
                }
            } else {
                if (item.locker.toString() !== lockerFilter) {
                    return;
                }
            }

            const itemMoment = moment(item.startDate).utc().format('YYYY-MM-DD');
            if (itemMoment !== selectedMoment) {
                return;
            }

            const key = `${item.locker}-${item.unitId}`;
            const availabilityItem = {
                startDate: item.startDate,
                endDate: item.endDate,
                timeSlot: [{ openTime: item.openTime, closeTime: item.closeTime }],
            };

            if (!groupedByUnit.has(key)) {
                groupedByUnit.set(key, {
                    locker: item.locker,
                    unitId: item.unitId,
                    unitName: item.unitName,
                    nickName: item.nickName,
                    locationId: item.locationId,
                    availability: [availabilityItem],
                });
            } else {
                const unitEntry = groupedByUnit.get(key)!;
                const existingAvailability = unitEntry.availability.find(
                    a => a.startDate === item.startDate && a.endDate === item.endDate
                );

                if (existingAvailability) {
                    existingAvailability.timeSlot.push({
                        openTime: item.openTime,
                        closeTime: item.closeTime,
                    });
                } else {
                    unitEntry.availability.push(availabilityItem);
                }
            }
        });

        return Array.from(groupedByUnit.values()).map(unit => ({
            ...unit,
            availability: unit.availability
                .map(a => ({
                    ...a,
                    timeSlot: a.timeSlot.sort((x, y) => x.openTime.localeCompare(y.openTime)),
                }))
                .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate)),
        }));
    }, []);

    // Get grouped availability
    const getGroupedAvailability = useCallback(async () => {
        try {
            if (!locationData?.location || !lockerWiseAvailabilityAndAppointmentsData) {
                return;
            }

            const lockerType = locationData?.location?.lockerTypeLockerName?.toLowerCase() || 'kiosk';
            const groupedAvailability = transformData(
                lockerWiseAvailabilityAndAppointmentsData,
                selectedDate.toString(),
                ((activeTab || 0) + 1).toString(),
                lockerType,
                unitId || ''
            );
            setGroupedDifferentUnitsAvailabilities(groupedAvailability);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to process availability data';
            setError(errorMessage);
            setShowToast(true);
        }
    }, [locationData, lockerWiseAvailabilityAndAppointmentsData, selectedDate, activeTab, unitId, transformData]);

    // Track fetched IDs to prevent duplicate calls
    const fetchedLocationIdRef = React.useRef<string | null>(null);
    const fetchedUnitIdRef = React.useRef<string | null>(null);

    // Fetch data on mount - only if not already fetched
    useEffect(() => {
        if (locationId && fetchedLocationIdRef.current !== locationId) {
            try {
                // Only fetch if location data doesn't exist or is for a different location
                const currentLocationId = locationData?.location?.id;
                if (!currentLocationId || currentLocationId !== locationId) {
                    dispatch(getLocationById(locationId));
                }
                dispatch(fetchLockerWiseAvailabilityAndAppointments(locationId));
                fetchedLocationIdRef.current = locationId;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch location data';
                setError(errorMessage);
                setShowToast(true);
            }
        }
    }, [locationId, dispatch, locationData]);

    useEffect(() => {
        if (unitId && fetchedUnitIdRef.current !== unitId) {
            try {
                // Only fetch if unit data doesn't exist or is for a different unit
                const currentUnitId = data?.id;
                if (!currentUnitId || currentUnitId !== unitId) {
                    dispatch(getUnit(unitId));
                }
                fetchedUnitIdRef.current = unitId;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch unit data';
                setError(errorMessage);
                setShowToast(true);
            }
        }
    }, [unitId, dispatch, data]);

    // Update grouped availability when dependencies change - call the memoized function directly
    useEffect(() => {
        getGroupedAvailability();
    }, [getGroupedAvailability]);

    // Update office hours when weekday or location data changes
    useEffect(() => {
        if (selectedWeekDay && locationData && Object.keys(locationData).length && locationData.officeHours?.length) {
            try {
                const officeHourData = locationData.officeHours.find(
                    (officeHour: { day: string; openTime: string; closeTime: string; isOpen: number }) => {
                        return officeHour.day === selectedWeekDay && officeHour.isOpen;
                    },
                );

                if (officeHourData) {
                    let openTime = officeHourData.openTime;
                    let closeTime = officeHourData.closeTime;

                    const openTimeMinutes = parseInt(moment(openTime, 'HH:mm').format('mm'));
                    if (openTimeMinutes > 0) {
                        openTime = moment(openTime, 'HH:mm').add(30, 'minutes').format('HH:mm');
                    }

                    const closeTimeMinutes = parseInt(moment(closeTime, 'HH:mm').format('mm'));
                    if (closeTimeMinutes > 0) {
                        closeTime = moment(closeTime, 'HH:mm').subtract(30, 'minutes').format('HH:mm');
                    }

                    const slots = generateTimeSlots(openTime, moment(closeTime, 'HH:mm').subtract(2, 'hours').format('HH:mm'));
                    setOfficeHoursOfWeekDay(slots || []);
                } else {
                    setOfficeHoursOfWeekDay([]);
                }
            } catch (err) {
                console.error('Error processing office hours:', err);
                setOfficeHoursOfWeekDay([]);
            }
        } else {
            setOfficeHoursOfWeekDay([]);
        }
    }, [selectedWeekDay, locationData]);

    // Process different units availabilities
    useEffect(() => {
        try {
            const { differentUnitsAvailabilities } = lockerWiseAvailabilityAndAppointmentsData.reduce(
                (
                    result: {
                        differentUnitsAvailabilities: LockerWiseAvailabilityAndAppointmentsResponse[];
                        selectedUnitsAvailabilities: LockerWiseAvailabilityAndAppointmentsResponse[];
                        processedDays: Set<number>;
                    },
                    item,
                ) => {
                    const startDate = moment(item.startDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
                    const date = moment(selectedDate).format('YYYY-MM-DD');
                    const differenceInDays = moment(date).diff(item.startDate);
                    const isDateWithinAvailabilityRange = date === startDate || differenceInDays <= 90;
                    const dayOfWeek = moment(item.startDate).day();

                    if (!result.processedDays.has(dayOfWeek) && item.locker === activeTab + 1 && isDateWithinAvailabilityRange) {
                        result.processedDays.add(dayOfWeek);

                        if (item.unitId !== unitId) {
                            result.differentUnitsAvailabilities[dayOfWeek] = item;
                        } else {
                            result.selectedUnitsAvailabilities.push(item);
                        }
                    }
                    return result;
                },
                { differentUnitsAvailabilities: [], selectedUnitsAvailabilities: [], processedDays: new Set<number>() },
            );

            const hourlySlots = differentUnitsAvailabilities.map((data) => {
                const openTime = moment(data.openTime, 'hh:mm A').format('HH:mm');
                const closeTime = moment(data.closeTime, 'hh:mm A').format('HH:mm');
                const slots = generateTimeSlots(openTime, moment(closeTime, 'hh:mm A').subtract(2, 'hours').format('HH:mm'));

                return {
                    id: data.id,
                    unitId: data.unitId,
                    name: data.unitName,
                    nickName: data.nickName,
                    availability: slots || [],
                    appointments: data.appointments,
                };
            });

            setDifferentUnitsAvailabilities(hourlySlots);
        } catch (err) {
            console.error('Error processing different units availabilities:', err);
        }
    }, [selectedDate, lockerWiseAvailabilityAndAppointmentsData, unitId, activeTab]);

    // Event handlers
    const onChange = useCallback((date: Date) => {
        const selectedWeekDay = moment(date).locale('en').format('dddd');
        setSelectedWeekDay(selectedWeekDay);
        setSelectedDate(date);
        setSelectedSlots([]);
    }, []);

    const checkboxHandler = useCallback(() => {
        setIsOfficeHoursChecked(!isOfficeHoursChecked);
        setSelectedSlots([]);
    }, [isOfficeHoursChecked]);

    const satSunAvailabilityHandler = useCallback(() => {
        if (satSunAvailability) {
            setSaturdaySlots([]);
            setSundaySlots([]);
        }
        setSatSunAvailability(!satSunAvailability);
    }, [satSunAvailability]);

    const onToggle = useCallback((value: number) => {
        setActiveIndex(activeIndex === value ? null : value);
    }, [activeIndex]);

    const addAvailabilityHandler = useCallback(() => {
        try {
            if (!locationData?.location || !data) {
                setError('Location or unit data is missing');
                setShowToast(true);
                return;
            }

            const availabilitySlots = isOfficeHoursChecked ? getTimeRangeFromSlots(officeHoursOfWeekDay) : slotRange;
            const startDate = moment(selectedDate).locale('en').format('D MMM, YYYY');
            const endDate = moment(selectedDate).add(90, 'days').locale('en').format('D MMM, YYYY');
            const satAvailabilitySlots = getContinuousTimeRanges(saturdaySlots);
            const sunAvailabilitySlots = getContinuousTimeRanges(sundaySlots);

            const availabilityObject = {
                locationName: locationData.location.name,
                locationId: locationData.location.id,
                unitId: unitId || '',
                unitName: data.name,
                startDate,
                endDate,
                locker: activeTab + 1,
                availabilitySlots,
                isOfficeHoursChecked,
                officeHours: locationData?.officeHours,
                satSunAvailability: satSunAvailability,
                saturdaySlots: satAvailabilitySlots,
                sundaySlots: sunAvailabilitySlots,
            };

            navigate('/add-availability/confirm', {
                state: { availabilityObject, type, locationId, unitId }
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to process availability';
            setError(errorMessage);
            setShowToast(true);
        }
    }, [
        locationData,
        data,
        isOfficeHoursChecked,
        officeHoursOfWeekDay,
        slotRange,
        selectedDate,
        saturdaySlots,
        sundaySlots,
        satSunAvailability,
        activeTab,
        unitId,
        locationId,
        type,
        navigate,
    ]);

    const handleBackClick = useCallback(() => {
        const locationName = locationData?.location?.name || '';
        navigate('/add-availability/units', {
            state: {
                unitId,
                locationId,
                type: 'add-availability',
                locationName
            }
        });
    }, [navigate, unitId, locationId, locationData]);

    // Memoized computed values
    const isConfirmDisabled = useMemo(() => {
        return (
            (groupedDifferentUnitsAvailabilities && groupedDifferentUnitsAvailabilities.length > 0) ||
            (isOfficeHoursChecked && (!officeHoursOfWeekDay || !officeHoursOfWeekDay.length)) ||
            (!isOfficeHoursChecked && !slotRange.length)
        );
    }, [groupedDifferentUnitsAvailabilities, isOfficeHoursChecked, officeHoursOfWeekDay, slotRange]);

    const lockersCount = useMemo(() => {
        return (locationData?.location?.lockersCount == 0)
            ? +locationData?.location.luxerLockersCount
            : locationData?.location?.lockersCount;
    }, [locationData]);

    // Auto-hide toast
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Error display effect
    useEffect(() => {
        if (hasError) {
            setError(hasError);
            setShowToast(true);
        }
    }, [hasError]);

    // Render loading state
    if (isLoading) {
        return <Loader />;
    }

    // Render error state
    if (hasError && !data) {
        return (
            <div className="outletConainer">
                <div className={styles.errorContainer}>
                    <p className={styles.errorText}>
                        {error || 'Failed to load data. Please try again.'}
                    </p>
                </div>
            </div>
        );
    }

    const SundayAvailability = () => (
        <CustomUnitAvailability
            selectedDate={selectedDate}
            setSelectedSlots={setSundaySlots}
            selectedSlots={sundaySlots}
        />
    );

    const SaturdayAvailability = () => (
        <CustomUnitAvailability
            selectedDate={selectedDate}
            setSelectedSlots={setSaturdaySlots}
            selectedSlots={saturdaySlots}
        />
    );

    return (
        <>
            <div className="outletConainer">
                <div className={styles.inlineItems}>
                    <div className={styles.leftSection}>
                        <div
                            onClick={handleBackClick}
                            className={styles.backButton}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleBackClick();
                                }
                            }}
                            aria-label="Go back to units"
                        >
                            <img src={iconBack} alt="back icon" />
                        </div>
                        <h2>Add Availability</h2>
                    </div>
                </div>
                <div className={`${styles.dateTimePickerContainer} date- time-picker`}>
                    <div className={`${styles.calendarWrapper} calendar-wrapper`}>
                        <Calendar
                            minDate={new Date(moment().format())}
                            onChange={onChange}
                            next2Label={null}
                            selectRange={false}
                            prev2Label={null}
                            allowPartialRange={true}
                            value={selectedDate}
                        />
                    </div>
                    <div className={styles.slotWrapper}>
                        <div className={styles.lockerTitleWrapper}>
                            <AvailabilityHeader
                                activeTab={activeTab}
                                data={data}
                                selectedDate={selectedDate}
                                setActiveTab={setActiveTab}
                                setSelectedSlots={setSelectedSlots}
                                lockers={lockersCount}
                                locationData={locationData}
                            />
                            <DifferentUnitAvailability
                                data={data}
                                differentUnitsAvailabilities={groupedDifferentUnitsAvailabilities || []}
                                locationData={locationData}
                            />

                            {!groupedDifferentUnitsAvailabilities?.length && (
                                <SelectedUnitAvailability
                                    data={data}
                                    officeHoursOfWeekDay={officeHoursOfWeekDay}
                                    isOfficeHoursChecked={isOfficeHoursChecked}
                                    selectedDate={selectedDate}
                                    setSelectedSlots={setSelectedSlots}
                                    selectedSlots={selectedSlots}
                                    setSlotRange={setSlotRange}
                                    selectedWeekDay={selectedWeekDay}
                                />
                            )}

                            {!groupedDifferentUnitsAvailabilities?.length && (
                                <div className={styles.availabilityContainer}>
                                    <p className={styles.availabilityTitle}>Set Availability</p>
                                    <div className={styles.officeHourCheckbox}>
                                        <label className={styles.label}>Use Office Hours Availability</label>
                                        <label className={styles.switch}>
                                            <input
                                                type="checkbox"
                                                id="officeHours"
                                                checked={isOfficeHoursChecked}
                                                onChange={checkboxHandler}
                                            />
                                            <span className={styles.slider}></span>
                                        </label>
                                    </div>

                                    <div className={styles.lowerContainer}>
                                        <div className={styles.officeHourCheckbox}>
                                            <label className={styles.label}>
                                                Set different availabilities for Saturday and Sunday
                                            </label>
                                            <label className={styles.switch}>
                                                <input
                                                    type="checkbox"
                                                    id="satSunAvailability"
                                                    checked={satSunAvailability}
                                                    onChange={satSunAvailabilityHandler}
                                                />
                                                <span className={styles.slider}></span>
                                            </label>
                                        </div>
                                        {satSunAvailability && (
                                            <Accordion
                                                title="Saturday availability"
                                                content={<SaturdayAvailability />}
                                                active={activeIndex === 0}
                                                onToggle={onToggle}
                                                index={0}
                                            />
                                        )}
                                        {satSunAvailability && (
                                            <Accordion
                                                title="Sunday availability"
                                                content={<SundayAvailability />}
                                                active={activeIndex === 1}
                                                onToggle={onToggle}
                                                index={1}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={addAvailabilityHandler}
                                disabled={isConfirmDisabled}
                                className={`${styles.confirmAvailability} ${isConfirmDisabled ? styles.disabled : styles.enabled}`}
                            >
                                Confirm New Availability
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {showToast && error && (
                <Toast type="error" message={error} />
            )}
        </>
    );
};

export default AddNewLocationAvailability;

